/**
 * AIProgressLoader — premium loading state for any AI tool.
 *
 * Shows a 4-step rotating message ("MarkeeMix is …") with a smooth indeterminate
 * progress bar. Use anywhere AI generation > 1.5s.
 *
 * <AIProgressLoader visible={loading} label="generating your report" />
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

const DEFAULT_STAGES = [
  "thinking through your request",
  "analysing the data",
  "drafting the result",
  "polishing the output",
];

export default function AIProgressLoader({
  visible = false,
  label = "generating your result",
  stages = DEFAULT_STAGES,
  variant = "card", // "card" | "inline" | "overlay"
}) {
  const [idx, setIdx] = useState(0);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (!visible) {
      setIdx(0);
      setPct(0);
      return;
    }
    const t1 = setInterval(() => setIdx((v) => (v + 1) % stages.length), 2200);
    // Asymptotic progress — never reaches 100 until visible=false
    const t2 = setInterval(() => {
      setPct((p) => (p < 88 ? p + Math.max(1, (88 - p) / 12) : p + 0.05));
    }, 220);
    return () => {
      clearInterval(t1);
      clearInterval(t2);
    };
  }, [visible, stages.length]);

  if (!visible) return null;

  const inner = (
    <div className="w-full" data-testid="ai-progress-loader">
      <div className="flex items-center gap-3 mb-3">
        <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-[#A855F7]/10">
          <motion.span
            animate={{ scale: [1, 1.3, 1], opacity: [0.7, 0.2, 0.7] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="absolute inset-0 rounded-full bg-[#A855F7]/20"
          />
          <Sparkles className="w-4 h-4 text-[#A855F7] relative" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#111111] leading-tight">
            <span className="font-bold">MarkeeMix</span> is {label}
          </p>
          <AnimatePresence mode="wait">
            <motion.p
              key={idx}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="text-xs text-slate-500 mt-0.5 truncate"
              data-testid="ai-progress-stage"
            >
              {stages[idx]}…
            </motion.p>
          </AnimatePresence>
        </div>
        <span className="text-xs font-mono text-slate-400 tabular-nums">
          {Math.min(99, Math.round(pct))}%
        </span>
      </div>

      <div className="relative h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#A855F7] to-[#9333EA]"
          animate={{ width: `${Math.min(95, pct)}%` }}
          transition={{ ease: "easeOut", duration: 0.3 }}
        />
        {/* shimmer */}
        <motion.span
          className="absolute inset-y-0 w-1/3 bg-white/40"
          animate={{ x: ["-30%", "330%"] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <p className="text-[11px] text-slate-400 mt-2.5">
        This usually takes 5–25 seconds. Please don&rsquo;t refresh.
      </p>
    </div>
  );

  if (variant === "overlay") {
    return (
      <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-3xl">
        <div className="w-full max-w-md p-6 rounded-3xl border border-slate-200 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
          {inner}
        </div>
      </div>
    );
  }

  if (variant === "inline") {
    return inner;
  }

  // default: card
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {inner}
    </div>
  );
}
